import { useState, useCallback } from 'react';

interface ImageGalleryState {
  images: string[];
  selectedImage: string | null;
  currentIndex: number;
  isLightboxOpen: boolean;
}

export const useImageGallery = (initialImages: string[] = []) => {
  const [state, setState] = useState<ImageGalleryState>({
    images: initialImages,
    selectedImage: null,
    currentIndex: 0,
    isLightboxOpen: false
  });

  const openLightbox = useCallback((imageUrl: string) => {
    const index = state.images.findIndex(img => img === imageUrl);
    setState(prev => ({
      ...prev,
      selectedImage: imageUrl,
      currentIndex: index >= 0 ? index : 0,
      isLightboxOpen: true
    }));
  }, [state.images]);

  const closeLightbox = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedImage: null,
      isLightboxOpen: false
    }));
  }, []);

  const nextImage = useCallback(() => {
    setState(prev => {
      const nextIndex = (prev.currentIndex + 1) % prev.images.length;
      return {
        ...prev,
        currentIndex: nextIndex,
        selectedImage: prev.images[nextIndex]
      };
    });
  }, []);

  const prevImage = useCallback(() => {
    setState(prev => {
      const prevIndex = prev.currentIndex === 0 ? prev.images.length - 1 : prev.currentIndex - 1;
      return {
        ...prev,
        currentIndex: prevIndex,
        selectedImage: prev.images[prevIndex]
      };
    });
  }, []);

  const addImages = useCallback((newImages: string[]) => {
    setState(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  }, []);

  const removeImage = useCallback((imageUrl: string) => {
    setState(prev => {
      const newImages = prev.images.filter(img => img !== imageUrl);
      const wasSelected = prev.selectedImage === imageUrl;
      
      return {
        ...prev,
        images: newImages,
        selectedImage: wasSelected ? null : prev.selectedImage,
        isLightboxOpen: wasSelected ? false : prev.isLightboxOpen,
        currentIndex: wasSelected ? 0 : prev.currentIndex
      };
    });
  }, []);

  const setImages = useCallback((images: string[]) => {
    setState(prev => ({
      ...prev,
      images,
      selectedImage: null,
      isLightboxOpen: false,
      currentIndex: 0
    }));
  }, []);

  return {
    images: state.images,
    selectedImage: state.selectedImage,
    currentIndex: state.currentIndex,
    isLightboxOpen: state.isLightboxOpen,
    openLightbox,
    closeLightbox,
    nextImage,
    prevImage,
    addImages,
    removeImage,
    setImages
  };
};